import type {
  DispatchAppendNewLegCommand,
  DispatchManifestDetailLeg,
  DispatchManifestDetailResponse,
} from '../models/dispatch-freight.models';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type DispatchSha256Digest = (value: Uint8Array) => Promise<ArrayBuffer>;

export class DispatchManifestSelectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DispatchManifestSelectionError';
  }
}

/**
 * Treats the manifest read as untrusted until it matches both the requested
 * manifest and the active customer. No append code should bypass this guard.
 */
export function assertDispatchManifestDetail(
  response: DispatchManifestDetailResponse,
  expectedManifestUuid: string,
  expectedCustomerUuid: string,
): DispatchManifestDetailResponse {
  const expectedManifest = normalizedUuid(expectedManifestUuid);
  const expectedCustomer = normalizedUuid(expectedCustomerUuid);
  const manifest = response.data.manifest;

  if (!expectedManifest || !expectedCustomer) {
    throw new DispatchManifestSelectionError('A valid customer and manifest are required.');
  }
  if (normalizedUuid(manifest.manifest_uuid) !== expectedManifest) {
    throw new DispatchManifestSelectionError('The manifest detail did not match the selected manifest.');
  }
  if (normalizedUuid(manifest.customer_contact_uuid) !== expectedCustomer) {
    throw new DispatchManifestSelectionError('The manifest does not belong to the active customer.');
  }
  if (!Number.isInteger(manifest.version) || manifest.version < 1) {
    throw new DispatchManifestSelectionError('The manifest does not have a usable authoritative version.');
  }
  if (manifest.lading_deleted === true) {
    throw new DispatchManifestSelectionError('The selected manifest is deleted and cannot accept a leg.');
  }

  const hasForeignLeg = response.data.legs.some((leg) => {
    const legManifest = normalizedUuid(leg.manifest_uuid);
    const legCustomer = normalizedUuid(leg.customer_contact_uuid);
    return (legManifest !== null && legManifest !== expectedManifest)
      || (legCustomer !== null && legCustomer !== expectedCustomer);
  });
  if (hasForeignLeg) {
    throw new DispatchManifestSelectionError('The manifest detail contains a leg outside the active customer scope.');
  }

  return response;
}

export function dispatchManifestPredecessorOptions(
  response: DispatchManifestDetailResponse,
): DispatchManifestDetailLeg[] {
  return response.data.legs
    .filter((leg) => leg.voided !== true)
    .slice()
    .sort((left, right) => {
      const sequenceDifference = sequenceValue(left) - sequenceValue(right);
      if (sequenceDifference !== 0) return sequenceDifference;
      return (left.created ?? '').localeCompare(right.created ?? '');
    });
}

export function selectDispatchManifestPredecessor(
  response: DispatchManifestDetailResponse,
  preferredLadingUuid?: string | null,
): string | null {
  const options = dispatchManifestPredecessorOptions(response);
  const preferred = normalizedUuid(preferredLadingUuid);
  if (preferred && options.some((leg) => normalizedUuid(leg.lading_uuid) === preferred)) {
    return preferred;
  }

  return options.length > 0
    ? normalizedUuid(options[options.length - 1].lading_uuid)
    : null;
}

/** Stable canonical JSON used as the retry identity for an identical command. */
export function canonicalDispatchJson(value: unknown): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return requiredJson(value);
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new Error('The append command contains a non-finite number.');
    }
    return requiredJson(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => canonicalDispatchJson(entry)).join(',')}]`;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const properties = Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalDispatchJson(record[key])}`);
    return `{${properties.join(',')}}`;
  }

  throw new Error('The append command contains a value that cannot be serialized.');
}

export async function buildDispatchManifestIdempotencyKey(
  command: DispatchAppendNewLegCommand,
  digest: DispatchSha256Digest = browserSha256,
): Promise<string> {
  const canonical = canonicalDispatchJson({
    manifest_uuid: command.manifest_uuid.toLowerCase(),
    request: command.request,
  });
  const hash = new Uint8Array(await digest(new TextEncoder().encode(canonical)));
  if (hash.byteLength !== 32) {
    throw new Error('The browser returned an invalid SHA-256 digest.');
  }

  const hex = Array.from(hash, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `dispatch:manifest-leg:v1:${hex}`;
}

function normalizedUuid(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return UUID_PATTERN.test(normalized) ? normalized : null;
}

function requiredJson(value: unknown): string {
  const encoded = JSON.stringify(value);
  if (encoded === undefined) {
    throw new Error('The append command contains a value that cannot be serialized.');
  }
  return encoded;
}

function sequenceValue(leg: DispatchManifestDetailLeg): number {
  return Number.isInteger(leg.manifest_leg_sequence)
    ? leg.manifest_leg_sequence as number
    : Number.MIN_SAFE_INTEGER;
}

async function browserSha256(value: Uint8Array): Promise<ArrayBuffer> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('This browser cannot create a retry-safe idempotency key.');
  }
  return subtle.digest('SHA-256', value);
}
